import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Shield, Upload, Copy} from 'lucide-react'
import SuperadminLayout from "../components/SuperadminLayout"

export default function SuperadminInvites() {
  const [activeTab, setActiveTab] = useState<"single" | "bulk" | "manage">("single")

  return (
    <SuperadminLayout activeMenuItem="invites">
      <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Tab Navigation */}
      <div className="flex items-center border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("single")}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === "single"
              ? "border-amber-600 text-amber-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Single Invite
        </button>
        <button
          onClick={() => setActiveTab("bulk")}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === "bulk"
              ? "border-amber-600 text-amber-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Bulk Invites
        </button>
        <button
          onClick={() => setActiveTab("manage")}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === "manage"
              ? "border-amber-600 text-amber-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Manage Invites
        </button>
      </div>

      {/* Tab Content */}
        {activeTab === "single" && <SingleInviteTab />}
        {activeTab === "bulk" && <BulkInvitesTab />}
        {activeTab === "manage" && <ManageInvitesTab />}
      </div>
    </SuperadminLayout>
  )
}

// Single Invite Tab
function SingleInviteTab() {
  const [formData, setFormData] = useState({
    role: "recruiter",
    region: "",
    email: "",
    name: "",
    ttl_days: 7
  })
  const [inviteUrl, setInviteUrl] = useState("")
  const [sending, setSending] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    
    try {
      const expiresAt = new Date(Date.now() + (formData.ttl_days * 24 * 60 * 60 * 1000))
      const token = btoa(JSON.stringify({
        role: formData.role,
        email: formData.email,
        region: formData.region,
        expires: expiresAt.getTime()
      }))
      
      const url = `${window.location.origin}/onboarding/role?invite=${formData.role}&token=${token}`
      setInviteUrl(url)

      await lumi.entities.Invites.create({
        role: formData.role,
        email: formData.email,
        name: formData.name,
        region: formData.region,
        invite_url: url,
        token: token,
        expires_at: expiresAt.toISOString(),
        status: "pending",
        created_by: lumi.auth.user?.email || "superadmin"
      })

      await sendInviteEmail(formData, url, expiresAt)
      toast.success(`Invite sent to ${formData.email} successfully!`)
    } catch (error) {
      console.error("Error generating invite:", error)
      toast.error("Failed to generate invite link")
    } finally {
      setSending(false)
    }
  }

  const sendInviteEmail = async (data: typeof formData, inviteUrl: string, expiresAt: Date) => {
    const roleLabels: Record<string, string> = {
      recruiter: "Recruiter",
      affiliate: "Affiliate",
      admin: "Admin"
    }

    const roleLabel = roleLabels[data.role] || data.role
    const adminEmail = lumi.auth.user?.email || "hello@nursinghive.com"

    try {
      // Send invite email directly to invitee
      await lumi.tools.email.send({
        to: data.email,
        subject: `You've been invited to join Nursing Hive as ${roleLabel}`,
        fromName: "Nursing Hive",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #0B253A; margin-bottom: 10px;">Welcome to Nursing Hive</h1>
              <p style="color: #5F7483; font-size: 16px;">You've been invited to join our platform</p>
            </div>
            
            <div style="background: #F4F7FA; padding: 25px; border-radius: 8px; margin: 20px 0;">
              <h2 style="color: #0FB5AE; margin-top: 0; font-size: 20px;">Invitation Details</h2>
              <p style="color: #0B253A; margin: 10px 0;"><strong>Role:</strong> ${roleLabel}</p>
              ${data.name ? `<p style="color: #0B253A; margin: 10px 0;"><strong>Name:</strong> ${data.name}</p>` : ""}
              ${data.region ? `<p style="color: #0B253A; margin: 10px 0;"><strong>Region:</strong> ${data.region}</p>` : ""}
              <p style="color: #0B253A; margin: 10px 0;"><strong>Invitation Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="${inviteUrl}" style="background: #0FB5AE; color: white; padding: 15px 40px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">Accept Invitation</a>
            </div>

            <div style="background: #FFF3CD; border-left: 4px solid #FFC107; padding: 15px; margin: 20px 0;">
              <p style="margin: 0; color: #856404;"><strong>Important:</strong> This invitation link will expire on ${expiresAt.toLocaleDateString()}. Please complete your registration before then.</p>
            </div>

            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #E0E0E0;">
              <p style="color: #5F7483; font-size: 14px; margin: 5px 0;">If you have any questions, please contact us at <a href="mailto:hello@nursinghive.com" style="color: #0FB5AE;">hello@nursinghive.com</a></p>
              <p style="color: #5F7483; font-size: 14px; margin: 5px 0;">© Nursing Hive — Ethical international nursing mobility</p>
            </div>
          </div>
        `,
        text: `Welcome to Nursing Hive\n\nYou've been invited to join our platform as ${roleLabel}.\n\n${data.name ? `Name: ${data.name}\n` : ""}${data.region ? `Region: ${data.region}\n` : ""}Invitation Expires: ${expiresAt.toLocaleDateString()}\n\nClick the link below to accept your invitation:\n${inviteUrl}\n\nImportant: This invitation link will expire on ${expiresAt.toLocaleDateString()}. Please complete your registration before then.\n\nIf you have any questions, contact us at hello@nursinghive.com`
      })

      // Send confirmation notification to admin
      await lumi.tools.email.send({
        to: adminEmail,
        subject: `Invite sent to ${data.email}`,
        fromName: "Nursing Hive System",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #0B253A;">Invite Confirmation</h2>
            <p style="color: #5F7483;">An invitation has been successfully sent to:</p>
            
            <div style="background: #E8F5E9; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #4CAF50;">
              <p style="margin: 8px 0; color: #0B253A;"><strong>Email:</strong> ${data.email}</p>
              <p style="margin: 8px 0; color: #0B253A;"><strong>Role:</strong> ${roleLabel}</p>
              ${data.name ? `<p style="margin: 8px 0; color: #0B253A;"><strong>Name:</strong> ${data.name}</p>` : ""}
              ${data.region ? `<p style="margin: 8px 0; color: #0B253A;"><strong>Region:</strong> ${data.region}</p>` : ""}
              <p style="margin: 8px 0; color: #0B253A;"><strong>Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
            </div>

            <p style="color: #5F7483; font-size: 14px;">The invitee will receive the invitation link directly and can register using their unique token.</p>
          </div>
        `,
        text: `Invite Confirmation\n\nAn invitation has been successfully sent to:\n\nEmail: ${data.email}\nRole: ${roleLabel}\n${data.name ? `Name: ${data.name}\n` : ""}${data.region ? `Region: ${data.region}\n` : ""}Expires: ${expiresAt.toLocaleDateString()}\n\nThe invitee will receive the invitation link directly.`
      })

    } catch (error) {
      console.error("Error sending invite email:", error)
      toast("Invite created but email failed. Copy link manually.", { icon: "⚠️" })
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl)
    toast.success("Invite URL copied to clipboard!")
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Single Invite</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            required
          >
            <option value="recruiter">Recruiter</option>
            <option value="affiliate">Affiliate</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Invitee Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="Full Name (optional)"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Invitee Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="user@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Expires in (days)</label>
          <input
            type="number"
            value={formData.ttl_days}
            onChange={(e) => setFormData({ ...formData, ttl_days: parseInt(e.target.value) })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            min="1"
            max="30"
          />
        </div>

        <button
          type="submit"
          disabled={sending}
          className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          {sending ? "Generating..." : "Generate Invite Link & Send Email"}
        </button>
      </form>

      {inviteUrl && (
        <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg max-w-2xl">
          <h3 className="font-semibold text-green-900 mb-3">Invite URL Generated</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={inviteUrl}
              readOnly
              className="flex-1 px-4 py-2 bg-white border border-green-300 rounded text-sm"
            />
            <button
              onClick={copyToClipboard}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Bulk Invites Tab  
function BulkInvitesTab() {
  const [formData, setFormData] = useState({
    role: "recruiter",
    region: "",
    ttl_days: 14,
    usage_limit: 1,
    emails: ""
  })
  const [processing, setProcessing] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    try {
      let emailList: string[] = []

      if (csvFile) {
        const text = await csvFile.text()
        const lines = text.split("\n").map(line => line.trim())
        emailList = lines.filter(line => line.includes("@"))
      } else if (formData.emails) {
        emailList = formData.emails
          .split(/[\n,]/)
          .map(e => e.trim())
          .filter(e => e.includes("@"))
      }

      if (emailList.length === 0) {
        toast.error("No valid emails found")
        return
      }

      const expiresAt = new Date(Date.now() + (formData.ttl_days * 24 * 60 * 60 * 1000))
      let successCount = 0

      for (const email of emailList) {
        try {
          const token = btoa(JSON.stringify({
            role: formData.role,
            email: email,
            region: formData.region,
            expires: expiresAt.getTime()
          }))

          const inviteUrl = `${window.location.origin}/onboarding/role?invite=${formData.role}&token=${token}`

          await lumi.entities.Invites.create({
            role: formData.role,
            email: email,
            region: formData.region,
            invite_url: inviteUrl,
            token: token,
            expires_at: expiresAt.toISOString(),
            status: "pending",
            created_by: lumi.auth.user?.email || "superadmin",
            usage_limit: formData.usage_limit
          })

          successCount++
        } catch (error) {
          console.error(`Failed to create invite for ${email}:`, error)
        }
      }

      toast.success(`Created ${successCount} invites successfully!`)
      setFormData({ ...formData, emails: "" })
      setCsvFile(null)
    } catch (error) {
      console.error("Error creating bulk invites:", error)
      toast.error("Failed to create bulk invites")
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Bulk Invite Generation</h2>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            required
          >
            <option value="recruiter">Recruiter</option>
            <option value="affiliate">Affiliate</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Region (optional)</label>
          <input
            type="text"
            value={formData.region}
            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="e.g., Pakistan, GCC"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Expires in (days)</label>
            <input
              type="number"
              value={formData.ttl_days}
              onChange={(e) => setFormData({ ...formData, ttl_days: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              min="1"
              max="60"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Usage per invite</label>
            <input
              type="number"
              value={formData.usage_limit}
              onChange={(e) => setFormData({ ...formData, usage_limit: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              min="1"
              max="5"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">CSV Upload</label>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
          <p className="text-sm text-gray-500 mt-2">Upload a CSV with an "email" column</p>
        </div>

        <div className="text-center text-gray-500 font-semibold">OR</div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Paste Emails</label>
          <textarea
            value={formData.emails}
            onChange={(e) => setFormData({ ...formData, emails: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            rows={6}
            placeholder="user1@example.com, user2@example.com&#10;or one per line"
          />
        </div>

        <button
          type="submit"
          disabled={processing}
          className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          {processing ? "Processing..." : "Generate Bulk Invites"}
        </button>
      </form>
    </div>
  )
}

// Manage Invites Tab
function ManageInvitesTab() {
  const [invites, setInvites] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchEmail, setSearchEmail] = useState("")
  const [filterRole, setFilterRole] = useState("")

  useEffect(() => {
    fetchInvites()
  }, [])

  const fetchInvites = async () => {
    try {
      const result = await lumi.entities.Invites.list({
        sort: { _id: -1 }
      })
      setInvites(result.list)
    } catch (error) {
      console.error("Error fetching invites:", error)
      toast.error("Failed to load invites")
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async (invite: any) => {
    try {
      const expiresAt = new Date(invite.expires_at)
      const roleLabels: Record<string, string> = {
        recruiter: "Recruiter",
        affiliate: "Affiliate",
        admin: "Admin"
      }
      const roleLabel = roleLabels[invite.role] || invite.role

      // Send invite email directly to invitee
      await lumi.tools.email.send({
        to: invite.email,
        subject: `You've been invited to join Nursing Hive as ${roleLabel}`,
        fromName: "Nursing Hive",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #0B253A; margin-bottom: 10px;">Welcome to Nursing Hive</h1>
              <p style="color: #5F7483; font-size: 16px;">You've been invited to join our platform</p>
            </div>
            
            <div style="background: #F4F7FA; padding: 25px; border-radius: 8px; margin: 20px 0;">
              <h2 style="color: #0FB5AE; margin-top: 0; font-size: 20px;">Invitation Details</h2>
              <p style="color: #0B253A; margin: 10px 0;"><strong>Role:</strong> ${roleLabel}</p>
              ${invite.name ? `<p style="color: #0B253A; margin: 10px 0;"><strong>Name:</strong> ${invite.name}</p>` : ""}
              ${invite.region ? `<p style="color: #0B253A; margin: 10px 0;"><strong>Region:</strong> ${invite.region}</p>` : ""}
              <p style="color: #0B253A; margin: 10px 0;"><strong>Invitation Expires:</strong> ${expiresAt.toLocaleDateString()} at ${expiresAt.toLocaleTimeString()}</p>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="${invite.invite_url}" style="background: #0FB5AE; color: white; padding: 15px 40px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">Accept Invitation</a>
            </div>

            <div style="background: #FFF3CD; border-left: 4px solid #FFC107; padding: 15px; margin: 20px 0;">
              <p style="margin: 0; color: #856404;"><strong>Important:</strong> This invitation link will expire on ${expiresAt.toLocaleDateString()}. Please complete your registration before then.</p>
            </div>

            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #E0E0E0;">
              <p style="color: #5F7483; font-size: 14px; margin: 5px 0;">If you have any questions, please contact us at <a href="mailto:hello@nursinghive.com" style="color: #0FB5AE;">hello@nursinghive.com</a></p>
              <p style="color: #5F7483; font-size: 14px; margin: 5px 0;">© Nursing Hive — Ethical international nursing mobility</p>
            </div>
          </div>
        `,
        text: `Welcome to Nursing Hive\n\nYou've been invited to join our platform as ${roleLabel}.\n\n${invite.name ? `Name: ${invite.name}\n` : ""}${invite.region ? `Region: ${invite.region}\n` : ""}Invitation Expires: ${expiresAt.toLocaleDateString()}\n\nClick the link below to accept your invitation:\n${invite.invite_url}\n\nImportant: This invitation link will expire on ${expiresAt.toLocaleDateString()}. Please complete your registration before then.\n\nIf you have any questions, contact us at hello@nursinghive.com`
      })
      
      toast.success(`Invite resent to ${invite.email}`)
    } catch (error) {
      console.error("Error resending invite:", error)
      toast.error("Failed to resend invite")
    }
  }

  const handleRevoke = async (invite: any) => {
    if (!confirm("This will invalidate the invite link. Continue?")) return
    
    try {
      await lumi.entities.Invites.update(invite._id, { status: "revoked" })
      toast.success("Invite revoked")
      fetchInvites()
    } catch (error) {
      console.error("Error revoking invite:", error)
      toast.error("Failed to revoke invite")
    }
  }

  const filteredInvites = invites.filter(invite => {
    const matchesEmail = !searchEmail || invite.email?.toLowerCase().includes(searchEmail.toLowerCase())
    const matchesRole = !filterRole || invite.role === filterRole
    return matchesEmail && matchesRole
  })

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Manage Invites</h2>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
        >
          <option value="">All Roles</option>
          <option value="recruiter">Recruiter</option>
          <option value="affiliate">Affiliate</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Loading invites...</p>
        </div>
      ) : filteredInvites.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p>No invites found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Created</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Expires</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInvites.map(invite => (
                <tr key={invite._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{invite.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      {invite.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      invite.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      invite.status === "used" ? "bg-green-100 text-green-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {invite.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right space-x-2">
                    {invite.status === "pending" && (
                      <>
                        <button
                          onClick={() => handleResend(invite)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => handleRevoke(invite)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
